import React from "react";
import { Link, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingBag, Tag, Users, LogOut, Mail, Star, BookOpen, Upload, MailCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const items = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/reminders", label: "Reminders", icon: MailCheck },
  { to: "/admin/blog", label: "Journal", icon: BookOpen },
  { to: "/admin/discounts", label: "Discounts", icon: Tag },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
  { to: "/admin/import", label: "Import", icon: Upload },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (loading) return <div className="min-h-screen bg-[#F5F3F0] flex items-center justify-center" data-testid="admin-loading">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#F5F3F0] flex" data-testid="admin-layout">
      <aside className="w-60 bg-[#1A1A1A] text-[#FDFBF7] flex flex-col fixed h-screen">
        <div className="px-6 py-6 border-b border-white/10">
          <p className="font-serif tracking-[0.28em] text-lg">DABKAZARI</p>
          <p className="text-[10px] uppercase tracking-nav text-[#BCA37F] mt-1">Atelier Console</p>
        </div>
        <nav className="flex-1 py-5">
          {items.map((it) => {
            const active = it.exact ? location.pathname === it.to : location.pathname.startsWith(it.to);
            const Icon = it.icon;
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`admin-nav-${it.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-6 py-2.5 text-sm transition-colors ${
                  active ? "bg-[#5C1A1B] text-[#FDFBF7]" : "text-[#FDFBF7]/70 hover:text-[#FDFBF7]"
                }`}
              >
                <Icon size={16} strokeWidth={1.4} />
                {it.label}
              </Link>
            );
          })}
        </nav>
        <div className="px-6 py-5 border-t border-white/10">
          <p className="text-[10px] uppercase tracking-nav text-[#BCA37F]">{user.email}</p>
          <button
            onClick={async () => { await logout(); navigate("/admin/login"); }}
            className="mt-3 flex items-center gap-2 text-xs uppercase tracking-nav text-[#FDFBF7]/70 hover:text-[#FDFBF7]"
            data-testid="admin-logout"
          >
            <LogOut size={14} strokeWidth={1.4} /> Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 ml-60 px-10 py-10">
        <Outlet />
      </main>
    </div>
  );
}
